/**
 * ROOM WAS ADDED — a plan was bought, or a plan moved up (wave 57).
 *
 * A conversation that meets the wall shows one control, and the purchase that
 * answers it happens somewhere else: the plan sheet that control opens. The
 * conversation hears about the purchase here — the same shape `lib/checkout.ts`
 * uses for a paid checkout, and for the same reason: the screen that bought and
 * the screen that was waiting do not share a provider. (A paid reset was the
 * third way room arrived; it is deleted, ruling R-0923-3 (1).)
 *
 * A PAID CHECKOUT IS ROOM ADDED TOO, and it is already counted where it
 * happens (`checkoutsPaid`), so the hook below reads both counters rather than
 * having the plan view announce a checkout a second time.
 *
 * COUNTERS AND NOT FLAGS, because what a waiting screen wants to know is
 * whether room was added SINCE IT STARTED WAITING, and a second purchase has to
 * be as visible as the first. Readers compare against what they saw at mount,
 * so nothing ever has to put a count back.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";
import { checkoutsPaid, noCheckoutsPaid, subscribeCheckout } from "./checkout";

let added = 0;
const listeners = new Set<() => void>();

/** Say that the allowance just grew: a plan moved up. */
export function roomAdded(): void {
  added += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function current(): number {
  return added;
}

/** The server render has bought nothing, and the number must be stable. */
function none(): number {
  return 0;
}

/**
 * Run `onAdded` each time room is added after this component mounted — an
 * upgrade, or a checkout that ended paid. The callback is read fresh
 * on every call, so a caller that decides what to do from its own state at
 * that moment need not memoise anything.
 */
export function useRoomAdded(onAdded: () => void): void {
  const count =
    useSyncExternalStore(subscribe, current, none) +
    useSyncExternalStore(subscribeCheckout, checkoutsPaid, noCheckoutsPaid);
  const seen = useRef(count);
  const latest = useRef(onAdded);
  latest.current = onAdded;
  useEffect(() => {
    if (count === seen.current) return;
    seen.current = count;
    latest.current();
  }, [count]);
}
